import { useEffect, useState } from 'react';
import { MOBILE_SHELL_QUERY } from '../utils/layoutBreakpoints';

/**
 * True when the viewport gets the MOBILE shell — the trigger for the "Mobile
 * Visual" conditional layout engine (Mobile Visual #171): the shell collapses
 * to a single full-screen column driven by the app-like overlay menu.
 *
 * Which viewports that is lives in ONE place, `utils/layoutBreakpoints.js`
 * (#432): below 1024px wide, or a 1024–1279px viewport 500px tall or less. The
 * CSS reads the same constant through Tailwind's `shell:` variant, so the JS
 * (menu drawer, forced full-screen panels) and the CSS (`hidden shell:flex` on
 * the sidebar + conversation column) flip in lockstep. It was width-only at
 * 768px before #432.
 *
 * Passing a numeric `breakpoint` keeps the old width-only meaning, for a
 * caller that genuinely wants "narrower than N px" rather than "which shell".
 * Uses matchMedia so it reacts on the exact boundary and on orientation
 * changes with a single listener.
 */
export function useIsMobile(breakpoint = null) {
    const query = breakpoint ? `(max-width: ${breakpoint - 0.02}px)` : MOBILE_SHELL_QUERY;
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false;
        return window.matchMedia(query).matches;
    });

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(query);
        const handler = (e) => setIsMobile(e.matches);
        // Sync once in case the media state changed between the initial render
        // and this effect running.
        setIsMobile(mq.matches);
        // Safari < 14 only supports the deprecated addListener signature.
        if (mq.addEventListener) mq.addEventListener('change', handler);
        else mq.addListener(handler);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handler);
            else mq.removeListener(handler);
        };
    }, [query]);

    return isMobile;
}

export default useIsMobile;
